import { Box, Button, Flex, Text, TextField } from "@radix-ui/themes";
import { Plus, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import RelationshipFieldPicker from "./RelationshipFieldPicker";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { ChartFilter, ChartFilterOp } from "@/types/breeze/ChartConfiguration";
import { IEntity } from "@/types/breeze/Entity";
import { Field, StandardFieldDatabaseType, StandardFieldDisplayType } from "@/types/breeze/Field";
import { entityService } from "@/services/breeze/EntityService";

interface Props {
  entityName: string;
  entity?: IEntity | null;
  value: ChartFilter[] | null | undefined;
  onChange: (filters: ChartFilter[]) => void;
}

const OPERATORS: { value: ChartFilterOp; label: string; needsValue: boolean }[] = [
  { value: "eq", label: "equals", needsValue: true },
  { value: "ne", label: "not equals", needsValue: true },
  { value: "gt", label: "greater than", needsValue: true },
  { value: "gte", label: "≥", needsValue: true },
  { value: "lt", label: "less than", needsValue: true },
  { value: "lte", label: "≤", needsValue: true },
  { value: "contains", label: "contains", needsValue: true },
  { value: "in", label: "in (comma-separated)", needsValue: true },
  { value: "exists", label: "exists", needsValue: false },
  { value: "not_exists", label: "does not exist", needsValue: false },
];

const BOOLEAN_OPTIONS = [
  { value: "true", label: "true" },
  { value: "false", label: "false" },
];

export default function ChartFilterBuilder({ entityName, entity, value, onChange }: Props) {
  const filters = value ?? [];

  const { data: fetchedEntity } = useQuery({
    queryKey: ["entity", entityName, true],
    queryFn: () => entityService.getByName(entityName, true),
    enabled: !entity && !!entityName,
    staleTime: 5 * 60 * 1000,
  });
  const effectiveEntity = entity ?? fetchedEntity ?? null;

  const setRow = (index: number, patch: Partial<ChartFilter>) => {
    const next = filters.map((f, i) => (i === index ? { ...f, ...patch } : f));
    onChange(next);
  };

  const addRow = () => {
    onChange([...filters, { field: "", op: "eq", value: "" }]);
  };

  const removeRow = (index: number) => {
    onChange(filters.filter((_, i) => i !== index));
  };

  const fieldByName = (name: string): Field | null => {
    if (!effectiveEntity || !name || name.includes(".")) return null;
    return effectiveEntity.fields.find((f) => f.name === name) ?? null;
  };

  return (
    <Flex direction="column" gap="2">
      <Flex justify="between" align="center">
        <Text as="label" size="2" weight="medium">Filters</Text>
        <Button size="1" variant="soft" type="button" onClick={addRow}>
          <Plus size={12} /> Add filter
        </Button>
      </Flex>
      {filters.length === 0 && (
        <Text size="1" color="gray">No filters — all records included.</Text>
      )}
      {filters.map((filter, i) => {
        const opDef = OPERATORS.find((o) => o.value === filter.op) ?? OPERATORS[0];
        const field = fieldByName(filter.field);
        return (
          <Flex key={i} gap="2" align="center" wrap="wrap">
            <Box style={{ flex: "1 1 220px", minWidth: 200 }}>
              <RelationshipFieldPicker
                entityName={entityName}
                value={filter.field || null}
                onChange={(newField) => setRow(i, { field: newField ?? "", value: "" })}
                placeholder="Field"
                width={220}
                clearable={false}
                allowRelationshipAsLeaf
              />
            </Box>
            <Box style={{ flex: "0 0 200px" }}>
              <SearchableSelect
                value={filter.op}
                onChange={(v) => setRow(i, { op: v as ChartFilterOp })}
                options={OPERATORS.map((o) => ({ value: o.value, label: o.label }))}
                placeholder="Operator"
                width={200}
              />
            </Box>
            <Box style={{ flex: "1 1 200px", minWidth: 160 }}>
              {opDef.needsValue ? (
                <FilterValueInput
                  field={field}
                  op={filter.op}
                  value={filter.value ?? ""}
                  onChange={(v) => setRow(i, { value: v })}
                />
              ) : (
                <Text size="1" color="gray">—</Text>
              )}
            </Box>
            <Button
              size="1"
              variant="soft"
              color="red"
              type="button"
              onClick={() => removeRow(i)}
              aria-label="Remove filter"
            >
              <Trash2 size={12} />
            </Button>
          </Flex>
        );
      })}
    </Flex>
  );
}

function FilterValueInput({
  field,
  op,
  value,
  onChange,
}: {
  field: Field | null;
  op: ChartFilterOp;
  value: string;
  onChange: (v: string) => void;
}) {
  const selectOptions = field?.display_type === StandardFieldDisplayType.SELECT
    ? field.options?.select?.options ?? null
    : null;
  const isBoolean = field?.database_type === StandardFieldDatabaseType.BOOLEAN;
  const isDate =
    field?.database_type === StandardFieldDatabaseType.DATE ||
    field?.database_type === StandardFieldDatabaseType.TIMESTAMP;
  const isNumeric =
    field?.database_type === StandardFieldDatabaseType.INTEGER ||
    field?.database_type === StandardFieldDatabaseType.BIGINTEGER ||
    field?.database_type === StandardFieldDatabaseType.DECIMAL;

  if (selectOptions && op !== "in" && op !== "contains") {
    return (
      <SearchableSelect
        value={value}
        onChange={(v) => onChange(v ?? "")}
        options={selectOptions.map((o) => ({ value: o.value, label: o.label }))}
        placeholder="Value"
        width={200}
      />
    );
  }

  if (isBoolean && op !== "in") {
    return (
      <SearchableSelect
        value={value}
        onChange={(v) => onChange(v ?? "")}
        options={BOOLEAN_OPTIONS}
        placeholder="Value"
        width={160}
      />
    );
  }

  if (isDate && (op === "eq" || op === "ne" || op === "gt" || op === "gte" || op === "lt" || op === "lte")) {
    const isDateOnly = field?.database_type === StandardFieldDatabaseType.DATE;
    const dateType = isDateOnly ? "date" : "datetime-local";
    const display = isDateOnly ? value : isoToLocalInput(value);
    return (
      <TextField.Root
        type={dateType}
        value={display}
        onChange={(e) => onChange(isDateOnly ? e.target.value : localInputToIso(e.target.value))}
      />
    );
  }

  return (
    <TextField.Root
      type={isNumeric && op !== "in" && op !== "contains" ? "number" : "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={op === "in" ? "value1, value2, value3" : "Value"}
    />
  );
}

function localInputToIso(local: string): string {
  if (!local) return "";
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? local : d.toISOString();
}

function isoToLocalInput(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
